import { Marker, Polyline } from "react-leaflet";
import L, { LatLngExpression } from "leaflet";
import { Map } from "@/components/ui/map";
import { BasemapLayer } from "@/components/map/BasemapLayer";
import { FitBounds } from "@/components/map/FitBounds";

function generateArcPoints(
  start: [number, number],
  end: [number, number],
  numPoints: number = 50,
): LatLngExpression[] {
  const points: LatLngExpression[] = [];

  // Validate coordinates
  if (
    !isFinite(start[0]) ||
    !isFinite(start[1]) ||
    !isFinite(end[0]) ||
    !isFinite(end[1])
  ) {
    return [];
  }

  // Handle same start/end coordinates
  const dx = end[1] - start[1];
  const dy = end[0] - start[0];
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) {
    return [];
  }

  const midLat = (start[0] + end[0]) / 2;
  const midLng = (start[1] + end[1]) / 2;

  const distance = length;
  const perpX = -dy;
  const perpY = dx;

  const arcHeight = distance * 0.15;
  const controlLat = midLat + (perpX / length) * arcHeight;
  const controlLng = midLng + (perpY / length) * arcHeight;

  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const lat =
      (1 - t) * (1 - t) * start[0] +
      2 * (1 - t) * t * controlLat +
      t * t * end[0];
    const lng =
      (1 - t) * (1 - t) * start[1] +
      2 * (1 - t) * t * controlLng +
      t * t * end[1];
    points.push([lat, lng]);
  }

  return points;
}

type Waypoint = {
  name: string;
  lat?: number | null;
  lng?: number | null;
};

export function TripMap({
  cityAName,
  cityBName,
  cityALat,
  cityALng,
  cityBLat,
  cityBLng,
  additionalWaypoints = [],
  zoomControl = true,
}: {
  cityAName: string;
  cityBName: string;
  cityALat?: number | null;
  cityALng?: number | null;
  cityBLat?: number | null;
  cityBLng?: number | null;
  zoomControl?: boolean;
  additionalWaypoints?: Waypoint[];
}) {
  const center = {
    lat: -25.2744,
    lng: 133.7751,
  };

  const isValidCoord = (lat?: number | null, lng?: number | null): boolean =>
    typeof lat === "number" &&
    typeof lng === "number" &&
    isFinite(lat) &&
    isFinite(lng);

  const cityACoords = isValidCoord(cityALat, cityALng)
    ? ([cityALat!, cityALng!] as [number, number])
    : null;

  const cityBCoords = isValidCoord(cityBLat, cityBLng)
    ? ([cityBLat!, cityBLng!] as [number, number])
    : null;

  const waypointCoords = additionalWaypoints
    .filter((wp) => isValidCoord(wp.lat, wp.lng))
    .map((wp) => [wp.lat!, wp.lng!] as [number, number]);

  const allCoords = [cityACoords, ...waypointCoords, cityBCoords].filter(
    (c): c is [number, number] => c !== null,
  );

  const mapCenter =
    allCoords.length > 0
      ? ([
          allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length,
          allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length,
        ] as [number, number])
      : ([center.lat, center.lng] as [number, number]);

  const bounds: L.LatLngBoundsExpression | null =
    allCoords.length >= 2 ? (allCoords as L.LatLngBoundsExpression) : null;

  const createCustomIcon = (label: string, color: string, cityName: string) => {
    return L.divIcon({
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
          <div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 14px; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${label}</div>
          <div style="background-color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; color: #1f2937; box-shadow: 0 2px 4px rgba(0,0,0,0.2); white-space: nowrap;">${cityName}</div>
        </div>
      `,
      className: "",
      iconSize: [30, 50],
      iconAnchor: [15, 25],
    });
  };

  return (
    <div className="relative">
      <Map
        center={mapCenter}
        zoom={4}
        scrollWheelZoom={false}
        zoomControl={zoomControl}
        height="240px"
      >
        <FitBounds bounds={bounds} maxZoom={12} />
        <BasemapLayer />

        {cityACoords ? (
          <Marker
            position={cityACoords}
            icon={createCustomIcon("A", "#3b82f6", cityAName)}
          />
        ) : null}

        {waypointCoords.map((coords, index) => {
          const waypoint = additionalWaypoints.filter((wp) => wp.lat && wp.lng)[
            index
          ];
          return (
            <Marker
              key={`waypoint-${index}`}
              position={coords}
              icon={createCustomIcon("B", "#10b981", waypoint.name)}
            />
          );
        })}

        {cityBCoords ? (
          <Marker
            position={cityBCoords}
            icon={createCustomIcon("B", "#10b981", cityBName)}
          />
        ) : null}

        {/* Draw polylines from A to each B */}
        {cityACoords
          ? [...waypointCoords, cityBCoords]
              .filter((c): c is [number, number] => c !== null)
              .filter(
                (coords) =>
                  coords[0] !== cityACoords[0] || coords[1] !== cityACoords[1],
              )
              .map((coords, index) => {
                const positions = generateArcPoints(cityACoords, coords);
                if (positions.length === 0) return null;
                return (
                  <Polyline
                    key={`line-${index}`}
                    positions={positions}
                    pathOptions={{
                      color: "#6366f1",
                      weight: 3,
                      opacity: 0.8,
                      dashArray: "8, 8",
                    }}
                  />
                );
              })
          : null}
      </Map>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2 font-medium text-white">
            <span>{cityAName}</span>
            {additionalWaypoints.map((wp, index) => (
              <span
                key={`wp-label-${index}`}
                className="flex items-center gap-2"
              >
                <span className="text-gray-200">→</span>
                <span>{wp.name}</span>
              </span>
            ))}
            <span className="text-gray-200">→</span>
            <span>{cityBName}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
